import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useMutation } from '@apollo/client/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
} from '@scio3/ui';
import {
  SWITCH_ACCOUNT_MEMBER_ACTION,
  type SwitchAccountMemberActionResult,
  type SwitchAccountMemberActionVariables,
} from '@scio3/graphql-client';
import { ROLE_TYPES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { usePermissions } from '@/hooks/usePermissions';
import { signOut, auth } from '@/lib/firebase';
import { SafeHtml, escapeHtml } from '@/components/shared/SafeHtml';

/** Display names for built-in role types — uppercase to match scio1 */
const ROLE_DISPLAY: Record<string, string> = {
  [ROLE_TYPES.OWNER]: 'OWNER',
  [ROLE_TYPES.ADMIN]: 'ADMIN',
  [ROLE_TYPES.INVITED]: 'INVITED',
  [ROLE_TYPES.USER]: 'USER',
  [ROLE_TYPES.CONTENT_PROPOSAL]: 'CONTENT PROPOSAL',
  [ROLE_TYPES.CONTENT_APPROVAL]: 'CONTENT APPROVAL',
  [ROLE_TYPES.READ_ONLY]: 'READ ONLY',
  [ROLE_TYPES.TEAM_OWNER]: 'TEAM OWNER',
  [ROLE_TYPES.SUPER_ADMIN]: 'SUPER ADMIN',
};

/**
 * Non-dismissible dialog shown when an existing user is invited to switch accounts.
 * Two variants:
 * - Regular user: warning about losing access to current account + No/Yes
 * - Root owner: warning about account deletion + DELETE confirmation + Cancel/Delete
 *
 * Matches scio2 InvitationDialogComponent behavior.
 */
export function InvitationAcceptanceDialog() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const inviteUser = useAuthStore((s) => s.inviteUser);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const { isRootOwner } = usePermissions();
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [switchAccount] = useMutation<
    SwitchAccountMemberActionResult,
    SwitchAccountMemberActionVariables
  >(SWITCH_ACCOUNT_MEMBER_ACTION);

  if (!inviteUser || !me) return null;

  const roleNew = ROLE_DISPLAY[inviteUser.role] ?? inviteUser.role;
  const nameNew = `${inviteUser.fullname || ''}(${inviteUser.email})`;
  const nameOld = `${me.ownerName || ''}(${me.ownerEmail})`;
  const activeTeam = me.teams.find((t) => t._id === activeTeamId);
  const currentTeamRole = activeTeam?.role ?? me.teams[0]?.role;
  const roleOld = currentTeamRole ? (ROLE_DISPLAY[currentTeamRole] ?? currentTeamRole) : '';

  const handleAction = async (accepted: boolean) => {
    // Root owner must type DELETE to accept
    if (accepted && isRootOwner && deleteConfirm !== 'DELETE') {
      setError(t('Please type') + ' "DELETE"');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await switchAccount({ variables: { payload: { inviteAccepted: accepted } } });
    } catch {
      // Proceed with signout regardless — backend state is already updated
    }
    await signOut(auth);
    window.location.reload();
  };

  // Build i18n interpolation values with escaped HTML
  const i18nValues = {
    roleNew: escapeHtml(roleNew),
    nameNew: escapeHtml(nameNew),
    nameOld: escapeHtml(nameOld),
    roleOld: escapeHtml(roleOld),
  };

  const messageKey = isRootOwner ? 'switchAccountModal1' : 'switchAccountModal2';

  return (
    <Dialog open>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        <DialogHeader>
          <DialogTitle>
            {t('Welcome')}, {me.firstName} {me.lastName}!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <SafeHtml html={t(messageKey, i18nValues)} />
            </AlertDescription>
          </Alert>

          {isRootOwner && (
            <div>
              <Label className="mb-2 block text-sm">
                {t('switchAccountModal3', { nameOld: escapeHtml(nameOld) })}
              </Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                autoFocus
                placeholder=""
              />
            </div>
          )}

          {error && <p className="text-center text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleAction(false)} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t(isRootOwner ? 'Cancel' : 'No')}
          </Button>
          <Button
            variant={isRootOwner ? 'destructive' : 'default'}
            onClick={() => handleAction(true)}
            disabled={loading || (isRootOwner && deleteConfirm !== 'DELETE')}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t(isRootOwner ? 'Delete' : 'Yes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
